import { createElement } from "react";
import { createRoot } from "react-dom/client";
import { EngineAdSlot } from "./EngineAdSlot";
const AD_ROOT_ID = "vninvestcharts-widget-ad-enforcer-root";
/** Default activation date for ad dialog. Before this date, ads are suppressed. */
const ADV_ACTIVATION_DATE = new Date('2027-01-01T00:00:00Z').getTime();
let _globalRoot = null;
let _globalEl = null;
let _subscriberCount = 0;
let _pendingUnmountTimer = null;
let _generation = 0;
let _currentValidatePat;
let _currentPat = "";
let _currentTier = "none";
let _currentCampaign = null;
let _currentLocale = undefined;
let _currentActivationDate = undefined;
function getOrCreatePortalRoot() {
    if (typeof document === "undefined") {
        throw new Error("document is not available");
    }
    let rootEl = document.getElementById(AD_ROOT_ID);
    if (!rootEl) {
        rootEl = document.createElement("div");
        rootEl.id = AD_ROOT_ID;
        rootEl.style.position = "fixed";
        rootEl.style.top = "0";
        rootEl.style.left = "0";
        rootEl.style.width = "0";
        rootEl.style.height = "0";
        rootEl.style.overflow = "visible";
        rootEl.style.zIndex = "2147483645";
        rootEl.style.pointerEvents = "none";
        document.body.appendChild(rootEl);
    }
    return rootEl;
}
function ensureGlobalRoot(validatePat) {
    if (!_globalRoot) {
        _globalEl = getOrCreatePortalRoot();
        _globalRoot = createRoot(_globalEl);
    }
    _currentValidatePat = validatePat;
    return _globalRoot;
}
let _enforcementEnabled = false;
function renderAd(pat, validatePat, tier = "none") {
    _generation++;
    const root = ensureGlobalRoot(validatePat);
    _currentPat = pat;
    if (_validatedTier) {
        _currentTier = _validatedTier;
    }
    else {
        _currentTier = tier;
    }
    if (!_enforcementEnabled) {
        // Enforcement disabled: only trademark badge shown (rendered by widget via EngineTrademark)
        // This enforcer does NOT render trademark anymore — widget owns badge rendering.
        root.render(null);
        return;
    }
    // Defense-in-depth: suppress ad before activation date
    const activationMs = _currentActivationDate ?? ADV_ACTIVATION_DATE;
    if (Date.now() < activationMs) {
        root.render(null);
        return;
    }
    root.render(createElement(EngineAdSlot, { pat: pat ?? "", validatePat, campaign: _currentCampaign, locale: _currentLocale, activationDate: _currentActivationDate }));
}
let _validatedTier = null;
async function validateServerTier(pat, validatePat) {
    if (!validatePat || !pat || !_globalRoot)
        return;
    try {
        const serverTier = await validatePat(pat);
        if (serverTier && serverTier !== _validatedTier) {
            _validatedTier = serverTier;
            _currentTier = serverTier;
            renderAd(_currentPat, _currentValidatePat, _currentTier);
        }
    }
    catch {
        if (_validatedTier !== "free") {
            _validatedTier = "free";
            _currentTier = "free";
            renderAd(_currentPat, _currentValidatePat, _currentTier);
        }
    }
}
/**
 * Imperative ad enforcer — mounts EngineAdSlot into document.body on engine init.
 * Called by EngineHost; cannot be disabled by widget code.
 * Uses a singleton React root shared across all EngineHost instances.
 * Trademark badge is now rendered by widget via EngineTrademark component.
 */
export function mountAdEnforcer(pat, validatePat, enforcementEnabled, campaign, tier, locale, activationDate) {
    _subscriberCount++;
    if (_subscriberCount === 1) {
        _enforcementEnabled = enforcementEnabled;
        _currentPat = pat ?? "";
        _currentTier = tier;
        _currentCampaign = campaign;
        _currentLocale = locale;
        _currentActivationDate = activationDate;
        renderAd(_currentPat, validatePat, _currentTier);
        if (pat)
            validateServerTier(pat, validatePat);
    }
    return {
        updatePat(pat) {
            _currentPat = pat ?? "";
            renderAd(_currentPat, _currentValidatePat, _currentTier);
            if (pat)
                validateServerTier(pat, _currentValidatePat);
        },
        updateTier(tier) {
            _currentTier = tier;
            _validatedTier = null;
            renderAd(_currentPat, _currentValidatePat, _currentTier);
        },
        updateEnforcement(enabled) {
            _enforcementEnabled = enabled;
            renderAd(_currentPat, _currentValidatePat, _currentTier);
        },
        updateCampaign(campaign) {
            _currentCampaign = campaign;
            renderAd(_currentPat, _currentValidatePat, _currentTier);
        },
        updateLocale(locale) {
            _currentLocale = locale;
            renderAd(_currentPat, _currentValidatePat, _currentTier);
        },
        updateActivationDate(date) {
            _currentActivationDate = date;
            renderAd(_currentPat, _currentValidatePat, _currentTier);
        },
        unmount() {
            _subscriberCount--;
            if (_subscriberCount <= 0) {
                if (_pendingUnmountTimer)
                    clearTimeout(_pendingUnmountTimer);
                _pendingUnmountTimer = setTimeout(() => {
                    if (_subscriberCount <= 0 && _globalRoot) {
                        // The host environment can be gone by the time this deferred
                        // unmount runs (a test that tore the environment down, an SSR
                        // pass). ReactDOM then throws "window is not defined", which
                        // surfaces as an unhandled error outside of any test. Reset the
                        // state without touching ReactDOM in that case.
                        if (typeof window === "undefined" || typeof document === "undefined") {
                            _globalRoot = null;
                            _globalEl = null;
                            _validatedTier = null;
                            _currentPat = "";
                            _currentTier = "none";
                            _currentCampaign = null;
                            _currentLocale = undefined;
                            _currentActivationDate = undefined;
                            _enforcementEnabled = false;
                            return;
                        }
                        _globalRoot.unmount();
                        _globalRoot = null;
                        if (_globalEl) {
                            _globalEl.remove();
                            _globalEl = null;
                        }
                        _validatedTier = null;
                        _currentPat = "";
                        _currentTier = "none";
                        _currentCampaign = null;
                        _currentLocale = undefined;
                        _currentActivationDate = undefined;
                        _enforcementEnabled = false;
                    }
                }, 1000);
            }
        },
    };
}
